/**
 * Repartidor.jsx
 * 
 * Página del repartidor para compartir su ubicación en tiempo real
 * 
 * Qué hace:
 * - Obtiene la geolocalización del navegador del repartidor
 * - Envía la posición a la API cada 2-3 segundos
 * - Muestra la posición actual en un mapa
 * - Muestra el estado de conexión
 * 
 * Funcionalidades:
 * - Geolocalización automática
 * - Envío automático de posición
 * - Visualización en mapa
 * - Indicadores de estado
 */

import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { guardarPosicionRepartidor } from '../services/api'
import MapaRepartidor from '../components/MapaRepartidor'
import './Repartidor.css'

function Repartidor() {
  const { linkId } = useParams()
  const [posicionActual, setPosicionActual] = useState(null)
  const [errorGeolocalizacion, setErrorGeolocalizacion] = useState(null)
  const [conectado, setConectado] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null)

  const watchIdRef = useRef(null)
  const intervaloEnvioRef = useRef(null)
  const ultimaPosicionEnviadaRef = useRef(null)
  const posicionActualRef = useRef(null) // Ref para la posición actual (para usar en el intervalo)
  const wakeLockRef = useRef(null) // Ref para Wake Lock

  /**
   * Calcula la distancia entre dos puntos en metros
   */
  const calcularDistancia = (lat1, lng1, lat2, lng2) => {
    const R = 6371000 // Radio de la Tierra en metros
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * Envía la posición actual a la API
   */
  const enviarPosicion = async (lat, lng) => {
    if (!linkId) return

    // Solo enviar si la posición cambió significativamente (>5 metros para pruebas)
    // Reducido de 10 a 5 metros para que sea más sensible en pruebas
    if (ultimaPosicionEnviadaRef.current) {
      const distancia = calcularDistancia(
        ultimaPosicionEnviadaRef.current.lat,
        ultimaPosicionEnviadaRef.current.lng,
        lat,
        lng
      )
      
      // Si no se movió más de 5 metros, no enviar
      if (distancia < 5) {
        console.log(`📍 Posición no cambió significativamente (${distancia.toFixed(2)}m), no enviando`)
        return
      }
    }

    setEnviando(true)

    try {
      await guardarPosicionRepartidor(linkId, lat, lng)
      
      ultimaPosicionEnviadaRef.current = { lat, lng }
      setUltimaActualizacion(Date.now())
      setConectado(true)
      console.log(`✅ Posición enviada: ${lat.toFixed(6)}, ${lng.toFixed(6)}`)
    } catch (error) {
      console.error('❌ Error enviando posición:', error)
      setConectado(false)
    } finally {
      setEnviando(false)
    }
  }

  /**
   * Inicia el seguimiento de geolocalización
   */
  const iniciarGeolocalizacion = () => {
    if (!navigator.geolocation) {
      setErrorGeolocalizacion('Tu navegador no soporta geolocalización')
      return
    }

    // Opciones para geolocalización de alta precisión
    const opciones = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    }

    // Obtener posición inicial
    navigator.geolocation.getCurrentPosition(
      (posicion) => {
        const nuevaPosicion = {
          lat: posicion.coords.latitude,
          lng: posicion.coords.longitude
        }
        setPosicionActual(nuevaPosicion)
        setErrorGeolocalizacion(null)
        
        // Enviar posición inicial
        enviarPosicion(nuevaPosicion.lat, nuevaPosicion.lng)
      },
      (error) => {
        console.error('Error obteniendo posición:', error)
        let mensaje = 'Error al obtener tu ubicación'
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            mensaje = 'Permiso de geolocalización denegado. Por favor, permite el acceso a tu ubicación.'
            break
          case error.POSITION_UNAVAILABLE:
            mensaje = 'Ubicación no disponible. Verifica que el GPS esté activado.'
            break
          case error.TIMEOUT:
            mensaje = 'Tiempo de espera agotado al obtener la ubicación.'
            break
        }
        
        setErrorGeolocalizacion(mensaje)
      },
      opciones
    )

    // Observar cambios en la posición
    watchIdRef.current = navigator.geolocation.watchPosition(
      (posicion) => {
        const nuevaPosicion = {
          lat: posicion.coords.latitude,
          lng: posicion.coords.longitude
        }
        // Actualizar tanto el state como el ref
        setPosicionActual(nuevaPosicion)
        posicionActualRef.current = nuevaPosicion
        setErrorGeolocalizacion(null)
      },
      (error) => {
        console.error('Error en watchPosition:', error)
      },
      opciones
    )

    // Enviar posición periódicamente (cada 2.5 segundos)
    // Usar el ref en lugar del state para evitar problemas de closure
    intervaloEnvioRef.current = setInterval(() => {
      const posicion = posicionActualRef.current
      if (posicion && posicion.lat && posicion.lng) {
        console.log(`🔄 Intervalo: Enviando posición periódica...`)
        enviarPosicion(posicion.lat, posicion.lng)
      } else {
        console.log('⏳ Intervalo: Esperando posición...')
      }
    }, 2500) // 2.5 segundos
  }

  /**
   * Detiene el seguimiento de geolocalización
   */
  const detenerGeolocalizacion = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current)
      watchIdRef.current = null
    }
    
    if (intervaloEnvioRef.current !== null) {
      clearInterval(intervaloEnvioRef.current)
      intervaloEnvioRef.current = null
    }
    
    // Liberar Wake Lock
    if (wakeLockRef.current !== null) {
      wakeLockRef.current.release().catch(err => {
        console.error('Error liberando Wake Lock:', err)
      })
      wakeLockRef.current = null
    }
    
    setConectado(false)
    setEnviando(false)
  }

  /**
   * Solicita Wake Lock para evitar que la pantalla se bloquee
   */
  const solicitarWakeLock = async () => {
    // Verificar si el navegador soporta Wake Lock
    if ('wakeLock' in navigator) {
      try {
        // Solicitar Wake Lock (solo funciona en contexto seguro - HTTPS o localhost)
        wakeLockRef.current = await navigator.wakeLock.request('screen')
        console.log('✅ Wake Lock activado - La pantalla no se bloqueará')
        
        // Escuchar cuando se libera el Wake Lock (por ejemplo, cuando el usuario cambia de pestaña)
        wakeLockRef.current.addEventListener('release', () => {
          console.log('⚠️ Wake Lock liberado')
        })
      } catch (error) {
        // Wake Lock puede fallar si:
        // - El navegador no lo soporta
        // - No está en contexto seguro (HTTPS)
        // - El usuario lo rechaza
        console.warn('⚠️ No se pudo activar Wake Lock:', error.message)
        // No es crítico, solo mostramos un aviso
      }
    } else {
      console.warn('⚠️ Tu navegador no soporta Wake Lock API')
    }
  }

  /**
   * Efecto para iniciar geolocalización cuando se monta el componente
   */
  useEffect(() => {
    if (linkId) {
      // Solicitar Wake Lock para evitar que la pantalla se bloquee
      solicitarWakeLock()
      
      // Iniciar geolocalización
      iniciarGeolocalizacion()
    }

    return () => {
      detenerGeolocalizacion()
    }
  }, [linkId])

  /**
   * Efecto para actualizar el ref cuando cambia la posición
   * Esto asegura que el intervalo siempre tenga la posición más reciente
   */
  useEffect(() => {
    if (posicionActual) {
      posicionActualRef.current = posicionActual
      // También enviar inmediatamente cuando cambia la posición (opcional, para respuesta más rápida)
      if (linkId && ultimaPosicionEnviadaRef.current === null) {
        // Solo enviar inmediatamente la primera vez
        enviarPosicion(posicionActual.lat, posicionActual.lng)
      }
    }
  }, [posicionActual, linkId])

  /**
   * Efecto para manejar la visibilidad de la página
   * Si la página se oculta y luego se muestra, reactivar Wake Lock
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && linkId) {
        // Si la página vuelve a ser visible, reactivar Wake Lock
        if (wakeLockRef.current === null) {
          solicitarWakeLock()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [linkId])

  /**
   * Formatea la fecha de última actualización
   */
  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'Nunca'
    
    const fecha = new Date(timestamp)
    const ahora = new Date()
    const diferencia = ahora - fecha

    if (diferencia < 1000) {
      return 'Ahora mismo'
    } else if (diferencia < 60000) {
      const segundos = Math.floor(diferencia / 1000)
      return `Hace ${segundos} ${segundos === 1 ? 'segundo' : 'segundos'}`
    } else {
      return fecha.toLocaleTimeString('es-AR')
    }
  }

  if (!linkId) {
    return (
      <div className="repartidor">
        <div className="repartidor-error">
          <h2>Link Inválido</h2>
          <p>El link de acceso no es válido. Por favor, verifica el link proporcionado.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="repartidor">
      <header className="repartidor-header">
        <h1>TuRutaTrack - Repartidor</h1>
        <p>Compartiendo tu ubicación en tiempo real</p>
      </header>

      <div className="repartidor-contenido">
        <aside className="repartidor-panel">
          <div className="panel-seccion">
            <h2>Estado de Conexión</h2>
            
            <div className={`estado-badge ${conectado ? 'conectado' : 'desconectado'}`}>
              {conectado ? (
                <>
                  <span className="estado-indicador conectado">●</span>
                  <span>Conectado y Enviando</span>
                </>
              ) : (
                <>
                  <span className="estado-indicador desconectado">●</span>
                  <span>Desconectado</span>
                </>
              )}
            </div>

            {enviando && (
              <div className="enviando-indicador">
                <span className="spinner"></span>
                <span>Enviando posición...</span>
              </div>
            )}

            {posicionActual && (
              <div className="info-posicion">
                <h3>Tu Ubicación Actual</h3>
                <div className="coordenadas">
                  <div className="coordenada-item">
                    <strong>Latitud:</strong>
                    <span>{posicionActual.lat.toFixed(6)}</span>
                  </div>
                  <div className="coordenada-item">
                    <strong>Longitud:</strong>
                    <span>{posicionActual.lng.toFixed(6)}</span>
                  </div>
                </div>
              </div>
            )}

            {ultimaActualizacion && (
              <div className="info-actualizacion">
                <strong>Última actualización enviada:</strong>
                <span>{formatearFecha(ultimaActualizacion)}</span>
              </div>
            )}

            {errorGeolocalizacion && (
              <div className="error-message">
                <strong>⚠️ Error:</strong>
                <p>{errorGeolocalizacion}</p>
                <button
                  className="btn-reintentar"
                  onClick={iniciarGeolocalizacion}
                >
                  🔄 Reintentar
                </button>
              </div>
            )}

            {!errorGeolocalizacion && !posicionActual && (
              <div className="advertencia">
                <p>📍 Obteniendo tu ubicación...</p>
                <p>Asegúrate de permitir el acceso a tu ubicación cuando el navegador lo solicite.</p>
              </div>
            )}
          </div>

          <div className="panel-seccion">
            <h2>Instrucciones</h2>
            <div className="instrucciones">
              <p>✅ Tu ubicación se está compartiendo automáticamente</p>
              <p>✅ El mapa se actualiza en tiempo real</p>
              <p>✅ Mantén esta página abierta mientras trabajas</p>
              <p>⚠️ No cierres esta pestaña si quieres seguir compartiendo tu ubicación</p>
            </div>
          </div>
        </aside>

        <main className="repartidor-mapa">
          {posicionActual ? (
            <MapaRepartidor posicionActual={posicionActual} />
          ) : (
            <div className="mapa-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📍</div>
                <h3>Obteniendo tu ubicación...</h3>
                <p>Por favor, permite el acceso a tu ubicación cuando el navegador lo solicite.</p>
                {errorGeolocalizacion && (
                  <button
                    className="btn-reintentar"
                    onClick={iniciarGeolocalizacion}
                  >
                    🔄 Reintentar
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default Repartidor

